import { useState, useEffect } from 'react'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import { getMatchingClientSite, getSitesDropdown, getTargetSite } from '../../../data/siteData'
import { getClientsDropdown, getTargetClient } from '../../../data/clientData'
import { getEmployeeDropdown } from '../../../data/employeeData'
import { getWorktypeDropdown } from '../../../data/worktypeData'
import {
    FormTextArea,
    FormDropDownList,
    FormDropDownListFilter,
    FormDatePicker,
    FormMultiSelect,
} from '../FormComponents'
import { dropDown, targetClientTypes } from '../../../data/interfaces'
import SkeletonForm from '../../Skeletons/SkeletonForm'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import Notes from '../../../pages/Notes/Notes';
import { addNoteDTO } from '../../../data/notes';
import { completeServiceReminder, createServiceReminder, deleteServiceReminder, getServiceReminderById, getServiceTypes, updateServiceReminder } from '../../../data/serviceReminderData';
import { ServiceRemidnerFull } from '../../../pages/ServiceReminders/serviceReminders.types';
import dayjs from 'dayjs';
import ServiceReminderBlock, { ServiceStep } from '../../../pages/ServiceReminders/ServiceReminderBlock';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { useToast } from '../../../contexts/ToastContext'
import { submitJob } from '../../../data/jobData'
import { useNavigate } from 'react-router-dom'
import { getAddressesForClientDropdown, getClientsForClientsDropdown } from '../../../data/accountsData'

interface Props {
    id: number
    mode: "edit" | "add"
    getServiceReminderData: (showCompleted: boolean, showDeleted: boolean) => void
    handleExpanded: () => void
    fromJob: { client: string, site: string, workType: string } | undefined
    onAddClientToggle: (type: string) => void
    onAddSiteToggle: (type: string) => void
    lockEdit: boolean
    setAddAccountVisible: (value: boolean) => void
    setAccountMode: (value: "edit" | "add") => void
    setSelectedAccountId: (value: number) => void
}

interface FormStateType {
  contact: { contactId: number }
  dueDate: string | null
  client: dropDown
  siteAddress: dropDown
  workType: dropDown
  description: string
  assignedTo?: dropDown
  employeeAssignedto: dropDown
  notify1DateTime: string | null
  notify2DateTime: string | null
  notify3DateTime: string | null
  notify1SentDateTime: string | null
  notify2SentDateTime: string | null
  notify3SentDateTime: string | null
  employeeCreatedById: number
  serviceTypes: dropDown[]
}

const EditServiceRemindersForm = ({ id, mode, getServiceReminderData, handleExpanded, fromJob, onAddClientToggle, onAddSiteToggle, lockEdit, setAddAccountVisible, setAccountMode, setSelectedAccountId }: Props) => {
    /** The selected item's full details */
    const [selectedItem, setSelectedItem] = useState<ServiceRemidnerFull | undefined>(undefined);
    /** Are we loading the selected item from the API? */
    const [loading, setLoading] = useState(false);
    /** The selected tab in the sidebar */
    const [selectedTab, setSelectedTab] = useState<number>(0);
    /** The customer associated to the item */
    const [customer, setCustomer] = useState<targetClientTypes | undefined>(undefined);
    /** All customers */
    const [allCustomers, setAllCustomers] = useState<dropDown[]>([]);
    /** Sites matched to a client */
    const [clientSites, setClientSites] = useState<dropDown[]>([]);
    /** Types of work */
    const [worktypeData, setWorktypeData] = useState<dropDown[]>([]);
    /** Employees for the assigned to field */
    const [employeeData, setEmployeeData] = useState<dropDown[]>([]);
    /** A dialog boolean for wether the complete dialog should be visible */
    const [completeDialogVisible, setCompleteDialogVisible] = useState(false);
    /** State for matching sites with cusrtomers */
    const [matchSiteActive, setMatchSiteActive] = useState(false)
    /** Toast hook to display toast */
    const { showToast } = useToast()
    /** Serice Type drop down */
    const [serviceType, setServiceType] = useState<dropDown[]>([]);

    const navigate = useNavigate();

    /**
     * Fetches the service types and sets them in the component state.
     * @returns {Promise<void>} A promise that resolves when the service types are fetched and set.
     */

    useEffect(() => {
      const init = async () => {
        // 1) fetch service-types
        const raw = await getServiceTypes();
        const formatted: dropDown[] = raw.map(s => ({
            value: s.id,
            text:  s.name,
        }));

        setServiceType(formatted);

        // 2) fetch other dropdowns
        await getAllCustomers();
        await getEmployees();
      };
      init();
    }, []);

    /** A blank from state */
    const blankFormState = {
        contact: {
            contactId: 0,
        },
        dueDate: "",
        client: {
            text: "",
            value: 0
        },
        siteAddress: {
            text: "",
            value: 0
        },
        workType: {
            text: "",
            value: 0
        },
        description: "",
        assignedTo: {
            text: "",
            value: 0
        },
        employeeAssignedto: {
            text: "",
            value: 0
        },
        notify1DateTime: null,
        notify2DateTime: null,
        notify3DateTime: null,
        notify1SentDateTime: null,
        notify2SentDateTime: null,
        notify3SentDateTime: null,
        employeeCreatedById: 0,
        serviceTypes: [] as dropDown[]
    }
    /** Form state */
    const [formState, setFormState] = useState<FormStateType>(blankFormState);

    /**
     * Represents an array of service steps.
     */
    const serviceSteps = (): ServiceStep[] => {
        if (formState) {
            const items: ServiceStep[] = [
                {
                    icon: "email",
                    daysRemaining: dayjs(formState.dueDate).diff(formState.notify1DateTime, 'days'),
                    completed: formState.notify1SentDateTime ? true : false,
                    expired: false,
                    date: formState.notify1DateTime,
                    identity: "notify1DateTime",
                    onChange: onBlockDateChange,
                    lockEdit: lockEdit
                },
                {
                    icon: "email",
                    identity: "notify2DateTime",
                    onChange: onBlockDateChange,
                    daysRemaining: dayjs(formState.dueDate).diff(formState.notify2DateTime, 'days'),
                    completed: formState.notify2SentDateTime ? true : false,
                    expired: false,
                    date: formState.notify2DateTime,
                    lockEdit: lockEdit
                },
                {
                    icon: "phone",
                    identity: "notify3DateTime",
                    onChange: onBlockDateChange,
                    daysRemaining: dayjs(formState.dueDate).diff(formState.notify3DateTime, 'days'),
                    completed: formState.notify3SentDateTime ? true : false,
                    expired: false,
                    date: formState.notify3DateTime,
                    lockEdit: lockEdit
                },
                {
                    icon: "expired",
                    identity: "dueDate",
                    onChange: onBlockDateChange,
                    daysRemaining: 0,
                    completed: false,
                    expired: true,
                    date: dayjs(dayjs(formState.dueDate).add(30, 'days')).format('YYYY-MM-DD'),
                    disabled: true,
                    lockEdit: lockEdit
                }
            ]
            return items
        } else {
            return []
        }
    }

    /**
     * Fetches site data based on the provided search string.
     * @param {string} search - The search string used to filter the site data.
     * @returns {Promise<void>} - A promise that resolves when the site data is fetched and set.
     */
    // const fetchSiteData = async (search: string): Promise<void> => {
    //     await getSitesDropdown(search).then((result) => {
    //         setClientSites(result);
    //         if (mode === "add" && fromJob !== undefined) {
    //             const site = result.find(x => x.text.includes(fromJob.site));
    //             if (site) {
    //                 setFormState((prev) => ({
    //                     ...prev,
    //                     siteAddress: {
    //                         text: site.text,
    //                         value: site.value
    //                     }
    //                 }))
    //             }
    //         }
    //     });
    // };

    /**
     * Retrieves the employee data for the dropdown.
     * @returns {Promise<void>} A promise that resolves when the employee data is fetched and set.
     */
    const getEmployees = async () => {
        const employees = await getEmployeeDropdown('');
        setEmployeeData(employees);
    }

    /**
     * Retrieves the work types and sets the worktypeData state.
     * This function is called when a selectedItem is available.
     */
    const getWorkTypes = async () => {
        const types = await getWorktypeDropdown();
        const defaultType = types.find(x => x.text === 'Breakdown and Servicing');
        if (!defaultType) return
        setFormState((prev) => ({
            ...prev,
            workType: {
                text: defaultType.text,
                value: defaultType.value
            }
        }))
        setWorktypeData(types);
    }


    /**
     * Searches for employees in the dropdown based on the provided search term.
     * @param searchTerm - The search term to filter the employee dropdown.
     */
    const searchEmployeeDropdown = (searchTerm: string) => {
        const fetchEmployeeData = async (search: string) => {
            await getEmployeeDropdown(search).then((result) => {
                setEmployeeData(result)
            })
        }

        fetchEmployeeData(searchTerm)
    }

    /**
     * Retrieves the selected service reminder item by its ID and sets it as the selected item.
     * @returns {Promise<void>} A promise that resolves when the selected item is retrieved and set.
     */
    const getSelectedItem = async () => {
        if (mode === 'edit') {
            setLoading(true)
            const item = await getServiceReminderById(id)
            setSelectedItem(item);
            
            setFormState((prev) => ({
                ...prev,
                notify1DateTime: item.notify1DateTime,
                notify2DateTime: item.notify2DateTime,
                notify3DateTime: item.notify3DateTime,
                notify1SentDateTime: item.notify1SentDateTime,
                notify2SentDateTime: item.notify2SentDateTime,
                notify3SentDateTime: item.notify3SentDateTime,
                dueDate: item.dueDate,
                contact: {
                    contactId: item.contactId
                },
                client: {
                    text: item.client,
                    value: item.clientId
                },
                employeeAssignedto: {
                    text: item.employeeAssignedto,
                    value: item.employeeAssignedtoId
                },
                siteAddress: {
                    text: item.siteName,
                    value: item.siteId
                },
                description: item.description,
                workType: {
                    text: item.workType,
                    value: item.workTypeId
                },
                serviceTypes: item.serviceTypes?.map((item) => ({
                    text: item?.name ?? '',
                    value: item?.serviceTypeId ?? 0,
                })) ?? [],
            }))
            setLoading(false)
        }
    }

    // Who to email about this reminder: its own contact, then the client's
    // primary contact, and only then the client record's own email - which
    // is often blank or left over from an import (e.g. "00"). The same order
    // the first hour invoice email uses.
    const looksLikeEmail = (value?: string | null) => !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
    const reminderContact = customer?.contacts?.find((c) => c.contactId === formState.contact?.contactId)
    const reminderContactName = [reminderContact?.firstname, reminderContact?.lastname].filter(Boolean).join(' ')
    const reminderEmail =
        [reminderContact?.email, customer?.primaryContact?.email, customer?.email].find(looksLikeEmail)?.trim() ?? ''

    /**
     * Retrieves the customer information for the selected item.
     * If a selected item exists, it calls the `getTargetClient` function to fetch the customer details
     * and updates the state with the retrieved customer.
     */
    const getCustomer = async () => {
        if (selectedItem) {
            const item = await getTargetClient(selectedItem.clientId);
            setCustomer(item);
        }
    }

    /**
     * Retrieves all customers and sets them in the state.
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    const getAllCustomers = async (query?: string) => {
        const items = await getClientsDropdown(query ? query : '');
        if (selectedItem) {
            const match = items.find(x => x.value === selectedItem.clientId)
            if (match) {
                setFormState((prev) => ({
                    ...prev,
                    client: {
                        text: match.text,
                        value: match.value
                    }
                }))
            }
        }
        setAllCustomers(items);

        if (query) {
            setFormState((prev) => ({
                ...prev,
                client: {
                    text: items[0].text,
                    value: items[0].value
                }
            }))
        }
    }

    /**
     * Handles the click event when the save button is clicked.
     * Updates the service reminder with the new form state values.
     */
    const saveClickHandler = async () => {
        if (selectedItem === undefined) return;
        updateServiceReminder({
            clientId: formState.client.value,
            siteId: formState.siteAddress.value,
            workTypeId: formState.workType.value,
            description: formState.description,
            notify1DateTime: formState.notify1DateTime ?? "",
            notify2DateTime: formState.notify2DateTime ?? "",
            notify3DateTime: formState.notify3DateTime ?? "",
            dueDate: formState.dueDate ?? "",
            employeeAssignedtoId: formState.employeeAssignedto.value,
            serviceReminderId: selectedItem.serviceReminderId,
            serviceTypes: formState.serviceTypes.map((item: any) => ({ serviceTypeId: item.value }))
        })
            .then(() => {
                getServiceReminderData(false, false)
            })
            .catch((error) => {
                console.error(error)
            })
    }

    const deleteClickHandler = () => {
        if (selectedItem === undefined) return;
        deleteServiceReminder(selectedItem.serviceReminderId, 0)
            .then(() => {
                getServiceReminderData(false, false)
            })
            .catch((error) => {
                console.error(error)
            })
    }

    /**
     * Handles the click event when the "Complete" button is clicked.
     * If a selected item exists, it calls the `completeServiceReminder` function
     * with the selected item's serviceReminderId. Then, it calls the `getServiceReminderData`
     * function to update the service reminder data. If an error occurs, it logs the error to the console.
     */
    const completeClickHander = (renew: boolean) => {
        if (selectedItem === undefined) return;
        completeServiceReminder(selectedItem.serviceReminderId, renew)
            .then(() => {
                getServiceReminderData(false, false);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const createClickHandler = () => {
        createServiceReminder({
            clientId: formState.client.value,
            siteId: formState.siteAddress.value,
            workTypeId: formState.workType.value,
            description: formState.description,
            notify1DateTime: formState.notify1DateTime ?? "",
            notify2DateTime: formState.notify2DateTime ?? "",
            notify3DateTime: formState.notify3DateTime ?? "",
            dueDate: formState.dueDate ?? "",
            employeeAssignedtoId: formState.employeeAssignedto.value,
            status: 0,
            serviceTypes: formState.serviceTypes.map((item: any) => ({ serviceTypeId: item.value }))
        }).then(() => {
            getServiceReminderData(false, false)
        }).catch((error) => {
            console.error(error)
        })
    }

    /**
     * Handles the change event for the due date field.
     * Updates the form state with the corresponding notification dates.
     */
    const onDueDateChange = () => {
        const dueDate = formState.dueDate;
        setFormState((prev) => ({
            ...prev,
            notify1DateTime: dayjs(dueDate, "DD-MM-YYYY").subtract(21, 'days').format('YYYY-MM-DD'),
            notify2DateTime: dayjs(dueDate, "DD-MM-YYYY").subtract(14, 'days').format('YYYY-MM-DD'),
            notify3DateTime: dayjs(dueDate, "DD-MM-YYYY").subtract(7, 'days').format('YYYY-MM-DD'),
        }))
    };

    /**
     * Handles the change event for the block date.
     * 
     * @param {string} key - The key of the form state property to update.
     * @param {string} value - The new value for the form state property.
     */
    const onBlockDateChange = (key: string, value: string) => {
        setFormState((prev) => ({
            ...prev,
            [key]: value
        }))
    }
    const searchSiteDropdown = (searchTerm: string) => {
        const fetchSiteData = async (search: string) => {
          await getSitesDropdown(search).then((result) => {
            setClientSites(result)
          })
        }
    
        fetchSiteData(searchTerm)
      }

    // /**
    //  * Fetches site data based on the provided search term and updates the state with the result.
    //  * @param {string} searchTerm - The search term used to filter the site data.
    //  */
    // const searchSiteDropdown = (searchTerm: string) => {
    //     fetchSiteData(searchTerm);
    // }
    /**
     * Checks if the form is valid. Disabled the save button if the form is invalid.
     * @returns {boolean} True if the form is valid, false otherwise.
     */
    const isFormValid = (): boolean => {
        if (formState.dueDate === "" || formState.client.value === 0 || formState.siteAddress.value === 0 || formState.workType.value === 0 || formState.employeeAssignedto.value === 0) {
            return true;
        } else {
            return false;
        }
    }

    useEffect(() => {
        if (formState.dueDate) {
            onDueDateChange()
        }
    }, [formState.dueDate])

    useEffect(() => {
        getCustomer();
    }, [selectedItem]);

    useEffect(() => {
        getSelectedItem()
    }, [id]);

    useEffect(() => {
        if (mode === 'add') {
            setFormState(() => ({
                ...blankFormState,
                dueDate: dayjs().format('YYYY-MM-DD')
            }));
        }
        getWorkTypes();

    }, [mode]);

    useEffect(() => {
        if (mode === "add" && fromJob !== undefined) {
            getAllCustomers(fromJob.client);
            // fetchSiteData(fromJob.site)
        }
    }, [mode, fromJob, worktypeData])

    const searchClientDropdown = (searchTerm: string) => {
        const fetchClientData = async (search: string) => {
            await getClientsDropdown(search).then((result) => {
                setAllCustomers(result)
            })
        }

        fetchClientData(searchTerm)
    }

    const addNoteDTO: addNoteDTO = {
        enquiryId: null,
        quoteId: null,
        jobId: null,
        serviceReminderId: selectedItem?.serviceReminderId
    };

    const convertToJob = () => {
        submitJob({
            clientId: formState.client.value,
            siteId: formState.siteAddress.value,
            description: formState.description,
            cost: 0,
            departmentId: 1,
            employeeProjectManagerId: formState.employeeAssignedto.value,
            endDate: dayjs().format('YYYY-MM-DD'),
            startDate: dayjs().format('YYYY-MM-DD'),
            enquiryId: null,
            enquirysourceId: 1,
            firstHourInvoiced: false,
            jobEmployeeIds: [],
            jobId: 0,
            priorityId: 1,
            quoteId: null,
            proformaCost: 0,
            purchaseOrder: "",
            worktypeId: 8,
            contactId: formState.contact.contactId,
        }).then((res) => {
            showToast('Job successfully created', 'success');
            const jobId = res.jobId;
            deleteServiceReminder(selectedItem?.serviceReminderId as number, jobId as number)
                .then(res => {
                    navigate(`/jobs?id=${jobId}`)
                })
                .catch(err => console.log(err))

        })
            .catch(err => {
                console.log(err)
            })
    }

    const handleCheckSiteExists = () => {
        const clientId = formState.client.value

        if (matchSiteActive === true) {
            getMatchingClientSite(clientId).then((result) => {
                if (result !== null && result !== undefined) {
                    const matchingId = (result as any).key

                    // const newSiteData = siteData.filter((item) => Number(item.value) === matchingId)

                    const fetchNewSiteData = async (id: number) => {
                        await getTargetSite(id).then((result: any) => {
                            const newSite = [{
                                value: result.siteId,
                                text: result.siteAddr,
                            }]

                            setClientSites(newSite)
                        })
                    }

                    fetchNewSiteData(matchingId)

                    showToast('Site matched', 'success')
                } else {
                    showToast('No matching site, try searching manually', 'error')
                }
            })
        } else {
            showToast('Select a client', 'error')
        }
    }


    const [clientId, setClientId] = useState(0)

    const getAndStoreAddressesForCustomer = async (customerId: number) => {
        try {
            const res = await getAddressesForClientDropdown(customerId);
            setClientSites(res);
            setFormState((prev) => ({
                ...prev,
                siteAddr: {
                  text: res.length === 1 ? res[0]?.text || '' : formState.siteAddress.text,
                  value: res.length === 1 ? res[0]?.value || 0 : formState.siteAddress.value,
                }
              }))
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        if (formState.client.value !== 0) {
            getAndStoreAddressesForCustomer(formState.client.value);
        }
    }, [formState.client.value])


    /**
     * End Account and address selection
     */

    // Contacts for client
    const [contactsForClient, setContactsForClient] = useState<dropDown[]>([]);

    const getAndStoreContactsForCustomer = async (customerId: number) => {
        try {
            const res = await getClientsForClientsDropdown(customerId);
            setContactsForClient(res);
            setFormState((prev: any) => ({
                ...prev,
                contact: {
                    contactId: res.length === 1 ? res[0]?.value || 0 : formState.contact.contactId,
                }
            }))

        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        if (formState.client.value !== 0) {
            getAndStoreContactsForCustomer(formState.client.value);
        }
    }, [formState.client.value])

    return (
        <>
            {
                loading && selectedItem === undefined && customer === undefined && clientSites.length === 0 && employeeData.length === 0 ? (
                    <SkeletonForm />
                ) : (
                    <div className='form__wrapper'>
                        <div style={{ display: 'flex', width: '100%' }}>
                            <TabStrip
                                selected={selectedTab}
                                onSelect={(e) => setSelectedTab(e.selected)}
                                className='form__tabstrip__container'
                            >
                                <TabStripTab title="Enquiry Form">
                                    <Form
                                        key={id}
                                        render={(formRenderProps) => (
                                            <FormElement style={{ padding: '1rem', width: '100%', marginBottom: '5rem' }} >
                                                <fieldset className={'k-form-fieldset'}>
                                                    {
                                                        mode === 'edit' && (
                                                            <div className='form__edit__container'>
                                                                <div className='form__title__background'>
                                                                    <div className='form__subtitle'>Edit reminder:</div>
                                                                </div>
                                                                <div className='form__details__subcontainer'>
                                                                    <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client: {customer?.clientName}</p>
                                                                    {reminderContactName && (
                                                                        <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Contact: {reminderContactName}</p>
                                                                    )}
                                                                    <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Address: {customer?.clientAddr}</p>
                                                                    <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Phone: {customer?.phone || customer?.phone2}</p>
                                                                    <p style={{ margin: 0, fontSize: "9pt", fontWeight: "400", letterSpacing: "normal" }}>Client Email: {reminderEmail}</p>
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                    <div style={{ margin: "10px 0 0", display: "flex", gap: "20px", justifyContent: "space-between" }}>
                                                        {
                                                            serviceSteps().map((step, index) => (
                                                                <ServiceReminderBlock key={index} {...step} />
                                                            ))
                                                        }
                                                    </div>
                                                    <Field
                                                        disabled={lockEdit}
                                                        name={'dueDate'}
                                                        component={FormDatePicker}
                                                        label={'Due Date:'}
                                                        defaultText={formState.dueDate}
                                                        defaultValue={formState.dueDate ? new Date(formState.dueDate) : new Date()}
                                                        onChange={(e) => {
                                                            setFormState((prev) => ({
                                                                ...prev,
                                                                dueDate: e.value
                                                            }))
                                                        }}
                                                    />

                                                    {/* <Field
                                                        disabled={lockEdit}
                                                        data={allCustomers}
                                                        // originalData={clientDataOrig}
                                                        name={'clientName'}
                                                        component={FormDropDownListFilter}
                                                        label={'Choose a client:'}
                                                        setData={setAllCustomers}
                                                        searchDropdown={searchClientDropdown}
                                                        defaultItem={formState.client}
                                                        onChange={(e) => {
                                                            setFormState({
                                                                ...formState,
                                                                client: {
                                                                    ...e.value,
                                                                    value: parseInt(e.value.value, 10),
                                                                },
                                                            })
                                                            setMatchSiteActive(true)
                                                        }}
                                                    hasButton={lockEdit ? false : true}
                                                    buttonText={'Add Client'}
                                                    buttonAction={onAddClientToggle}
                                                    /> */}
                                                    <Field
                                                        disabled={lockEdit}
                                                        data={allCustomers}
                                                        name={'clientName'}
                                                        component={FormDropDownListFilter}
                                                        label={'Choose a client:'}
                                                        setData={setAllCustomers}
                                                        searchDropdown={searchClientDropdown}
                                                        defaultItem={formState.client}
                                                        onChange={(e) => {
                                                            setClientId(parseInt(e.value.value, 10))
                                                            setFormState({
                                                                ...formState,
                                                                client: {
                                                                    ...e.value,
                                                                    value: parseInt(e.value.value, 10),
                                                                },
                                                            })
                                                        }}
                                                        hasButton={lockEdit ? false : true}
                                                        buttonText={'Add Account'}
                                                        buttonAction={() => setAddAccountVisible(true)}
                                                    />

                                                    <Field
                                                        disabled={lockEdit}
                                                        data={contactsForClient}
                                                        name={'contact'}
                                                        required={true}
                                                        component={FormDropDownListFilter}
                                                        label={'Contact:'}
                                                        setData={setContactsForClient}
                                                        onOpen={() => getAndStoreContactsForCustomer(formState.client.value)}
                                                        searchDropdown={searchSiteDropdown}
                                                        defaultItem={contactsForClient.find(item => Number(item.value) === Number(formState.contact.contactId))}
                                                        onChange={(e) => {
                                                            setFormState({
                                                                ...formState,
                                                                contact: {
                                                                    ...e.value,
                                                                    contactId: parseInt(e.value.value, 10),
                                                                }
                                                            })
                                                        }}
                                                        hasButton={lockEdit ? false : true}
                                                        buttonText={'Add Contact'}
                                                        buttonAction={() => {
                                                            setAccountMode("edit");
                                                            setSelectedAccountId(formState.client.value);
                                                            setAddAccountVisible(true)
                                                        }}
                                                    />

                                                    <Field
                                                        disabled={lockEdit}
                                                        data={clientSites}
                                                        name={'siteAddr'}
                                                        component={FormDropDownList}
                                                        label={'Address:'}
                                                        defaultItem={clientSites.find(item => Number(item.value) === Number(formState.siteAddress.value))}
                                                        setData={setClientSites}
                                                        onOpen={() => getAndStoreAddressesForCustomer(formState.client.value)}
                                                        searchDropdown={searchSiteDropdown}
                                                        onChange={(e: any) => {
                                                            setFormState({
                                                                ...formState,
                                                                siteAddress: { ...e.value, value: parseInt(e.value.value, 10) },
                                                            })
                                                        }}
                                                        hasButton={lockEdit ? false : true}
                                                        buttonText={'Add Address'}
                                                        buttonAction={() => {
                                                            setAccountMode("edit");
                                                            setSelectedAccountId(formState.client.value);
                                                            setAddAccountVisible(true)
                                                        }}
                                                    />

                                                    <Field
                                                        disabled={lockEdit}
                                                        data={worktypeData}
                                                        name={'workType'}
                                                        component={FormDropDownList}
                                                        label={'Work Type:'}
                                                        defaultItem={formState.workType}
                                                        onChange={(e) => {
                                                            setFormState({
                                                                ...formState,
                                                                workType: {
                                                                    ...e.value,
                                                                    value: parseInt(e.value.value, 10),
                                                                },
                                                            })
                                                        }}
                                                    />

                                                    {formState.workType.text.includes("Servic") && (
                                                        <Field
                                                            disabled={lockEdit}
                                                            data={serviceType}
                                                            name="serviceType"
                                                            component={FormMultiSelect}
                                                            defaultValue={formState.serviceTypes || []}
                                                            label="Service Type:"
                                                            textField="text"              // <-- use textField
                                                            dataItemKey="value"         // <-- use dataItemKey
                                                            onChange={e => setFormState({ ...formState, serviceTypes: e.value as dropDown[] })}
                                                        />
                                                    )}

                                                    {/* <Field
                                                        disabled={lockEdit}
                                                        data={clientSites}
                                                        name={'siteAddr'}
                                                        component={FormDropDownListFilter}
                                                        defaultItem={formState.siteAddress}
                                                        label={'Site Address:'}
                                                        setData={setAllCustomers}
                                                        searchDropdown={searchSiteDropdown}
                                                        hasButton={lockEdit ? false : true}
                                                        buttonText={'Add Site'}
                                                        buttonAction={onAddSiteToggle}
                                                        hasSecondButton={lockEdit ? false : true}
                                                        secondButtonText={'Match site'}
                                                        secondButtonAction={handleCheckSiteExists}
                                                        onChange={(e: any) => {
                                                            setFormState({
                                                                ...formState,
                                                                siteAddress: { ...e.value, value: parseInt(e.value.value, 10) },
                                                            })
                                                        }}
                                                    /> */}

                                                    <Field
                                                        disabled={lockEdit}
                                                        name={'description'}
                                                        required={true}
                                                        component={FormTextArea}
                                                        label={'Description:'}
                                                        value={formState.description}
                                                        defaultValue={formState.description}
                                                        onChange={(e) => {
                                                            setFormState({ ...formState, description: e.value })
                                                        }}
                                                    />
                                                    <Field
                                                        disabled={lockEdit}
                                                        data={employeeData}
                                                        name={'employeeAssignedToId'}
                                                        component={FormDropDownListFilter}
                                                        label={'Assigned to:'}
                                                        searchDropdown={searchEmployeeDropdown}
                                                        defaultItem={formState.employeeAssignedto}
                                                        onChange={(e) => {
                                                            setFormState({
                                                                ...formState,
                                                                employeeAssignedto: {
                                                                    ...e.value,
                                                                    value: parseInt(e.value.value, 10),
                                                                },
                                                            })
                                                        }}
                                                    />
                                                    {
                                                        lockEdit === false ? (
                                                            <>
                                                                <div className='button__container'>
                                                                    {
                                                                        mode === 'edit' && (
                                                                            <button
                                                                                disabled={isFormValid()}
                                                                                onClick={() => {
                                                                                    saveClickHandler()
                                                                                    formRenderProps.onFormReset()
                                                                                    handleExpanded()
                                                                                }}
                                                                                className='form__edit__button edit'
                                                                            >
                                                                                Save changes
                                                                            </button>
                                                                        )
                                                                    }
                                                                    {
                                                                        mode === 'edit' && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    setCompleteDialogVisible(true)
                                                                                }}
                                                                                className='form__edit__button accept'
                                                                            >
                                                                                Mark as complete
                                                                            </button>
                                                                        )
                                                                    }
                                                                    {
                                                                        mode === 'edit' && (
                                                                            <button
                                                                                onClick={() => {
                                                                                    deleteClickHandler()
                                                                                    formRenderProps.onFormReset()
                                                                                    handleExpanded()
                                                                                }}
                                                                                className='form__edit__button reject'
                                                                            >
                                                                                Delete reminder
                                                                            </button>
                                                                        )
                                                                    }
                                                                    {
                                                                        mode === 'add' && (
                                                                            <button
                                                                                disabled={isFormValid()}
                                                                                onClick={() => {
                                                                                    createClickHandler()
                                                                                    formRenderProps.onFormReset()
                                                                                    handleExpanded()
                                                                                }}
                                                                                className='form__edit__button accept'
                                                                            >
                                                                                Create reminder
                                                                            </button>
                                                                        )
                                                                    }
                                                                </div>
                                                                {
                                                                    mode === 'edit' && (

                                                                        <div className="button__container" style={{ paddingTop: "0" }}>
                                                                            <button
                                                                                onClick={() => convertToJob()}
                                                                                className='form__edit__button accept'
                                                                            >
                                                                                Convert to Job
                                                                            </button>
                                                                        </div>
                                                                    )
                                                                }
                                                            </>
                                                        ) : null
                                                    }
                                                </fieldset>
                                            </FormElement>
                                        )}
                                    />
                                </TabStripTab>
                                <TabStripTab title="Notes">
                                    <Notes lockEdit={lockEdit} sourceEntity={addNoteDTO} />
                                </TabStripTab>
                            </TabStrip>
                        </div>
                        {
                            completeDialogVisible && (
                                <Dialog title={"Please confirm"} onClose={() => setCompleteDialogVisible(false)}>
                                    <p
                                        style={{
                                            margin: "25px",
                                            textAlign: "center",
                                        }}
                                    >
                                        Do you want to create a new Service Reminder for this client, site and customer, one year from the current date ({dayjs().add(1, 'year').format('DD-MM-YYYY')}?)
                                    </p>
                                    <DialogActionsBar>
                                        <button
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                            onClick={() => {
                                                setCompleteDialogVisible(false)
                                                completeClickHander(false)
                                                handleExpanded()
                                            }}
                                        >
                                            No, just mark as complete
                                        </button>
                                        <button
                                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                                            onClick={() => {
                                                completeClickHander(true)
                                                handleExpanded()
                                            }}
                                        >
                                            Yes and mark as complete
                                        </button>
                                    </DialogActionsBar>
                                </Dialog>
                            )
                        }
                    </div>
                )
            }
        </>
    )
}

export default EditServiceRemindersForm